import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * RoleSelectionPage - Landing page with sliding panels for role selection
 * Features full-screen split panels that slide to reveal login forms
 */

// Professional Icon Components
const StudentIcon = () => (
  <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
  </svg>
);

const TeacherIcon = () => (
  <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-6M18 7v6" />
  </svg>
);

const SchoolAdminIcon = () => (
  <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const PlatformAdminIcon = () => (
  <svg className="w-12 h-12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const RoleSelectionPage = () => {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState(null);
  const [hoveredRole, setHoveredRole] = useState(null);

  const roles = [
    {
      id: 'student',
      title: 'Student',
      description: 'Learn with AI assistance',
      icon: StudentIcon,
      accentColor: '#8B5CF6', // violet-500
      iconGradient: 'from-violet-500 to-purple-600',
      buttonGradient: 'from-violet-600 to-purple-700',
      path: '/login/student',
      registerPath: '/register/student'
    },
    {
      id: 'teacher',
      title: 'Teacher',
      description: 'Manage your classroom',
      icon: TeacherIcon,
      accentColor: '#06B6D4', // cyan-500
      iconGradient: 'from-cyan-500 to-blue-600',
      buttonGradient: 'from-cyan-600 to-blue-700',
      path: '/login/teacher',
      registerPath: '/register/teacher'
    },
    {
      id: 'school-admin',
      title: 'School Admin',
      description: 'Manage your school',
      icon: SchoolAdminIcon,
      accentColor: '#EC4899', // pink-500
      iconGradient: 'from-pink-500 to-rose-600',
      buttonGradient: 'from-pink-600 to-rose-700',
      path: '/login/school-admin',
      registerPath: '/register/school-admin'
    },
    {
      id: 'super-admin',
      title: 'Platform Admin',
      description: 'Manage all schools',
      icon: PlatformAdminIcon,
      accentColor: '#64748B', // slate-500
      iconGradient: 'from-slate-600 to-slate-700',
      buttonGradient: 'from-slate-700 to-slate-800',
      path: '/login/super-admin',
      registerPath: '/register/super-admin'
    }
  ];

  const handleRoleClick = (roleId) => {
    setSelectedRole(roleId);
  };

  const handleBack = () => {
    setSelectedRole(null);
  };

  const handleLogin = (path) => {
    navigate(path);
  };

  const handleRegister = (path) => {
    navigate(path);
  };

  return (
    <div className="h-screen w-screen overflow-hidden relative route-container">
      {/* Unified Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-violet-600 via-cyan-500 via-pink-500 to-slate-700" />

      {/* Animated Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 via-transparent to-blue-600/20 animate-gradient" />

      {/* Mesh Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, white 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }} />
      </div>

      {/* Logo Overlay - Always Visible */}
      <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-50">
        <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-full px-8 py-3 shadow-2xl">
          <h1 className="text-3xl font-bold text-white tracking-tight drop-shadow-lg">
            Brillabee
          </h1>
        </div>
      </div>

      {/* Role Panels Container */}
      <div className="flex h-full w-full">
        {roles.map((role) => {
          const isSelected = selectedRole === role.id;
          const isAnySelected = selectedRole !== null;
          const isHovered = hoveredRole === role.id;
          const IconComponent = role.icon;

          return (
            <div
              key={role.id}
              className={`relative transition-all duration-700 ease-in-out ${
                isAnySelected
                  ? isSelected
                    ? 'w-full'
                    : 'w-0 opacity-0'
                  : 'w-1/4'
              }`}
            >
              {/* Role Panel */}
              <div
                className="h-full w-full relative overflow-hidden cursor-pointer"
                style={{
                  transition: 'all 0.8s cubic-bezier(0.54, 0.35, 0.29, 0.99)'
                }}
                onClick={() => !isAnySelected && handleRoleClick(role.id)}
                onMouseEnter={() => setHoveredRole(role.id)}
                onMouseLeave={() => setHoveredRole(null)}
              >
                {/* Subtle Separator Line */}
                {!isSelected && (
                  <div className="absolute right-0 top-1/4 bottom-1/4 w-px bg-gradient-to-b from-transparent via-white/20 to-transparent" />
                )}

                {/* Hover Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-b from-white/0 to-white/0 transition-all duration-500 ${
                  isHovered && !isAnySelected ? 'from-white/5 to-white/10' : ''
                }`} />

                {/* Content - Role Selection View */}
                {!isSelected && (
                  <div className="relative h-full flex flex-col items-center justify-center text-white p-6">
                    {/* Icon Container */}
                    <div className={`mb-6 transform transition-all duration-500 ${
                      isHovered ? 'scale-110 -translate-y-3' : 'scale-100'
                    }`}>
                      <div className="relative">
                        {/* Glow Effect */}
                        <div className={`absolute inset-0 blur-2xl opacity-0 transition-opacity duration-500 ${
                          isHovered ? 'opacity-50' : ''
                        }`} style={{ backgroundColor: role.accentColor }} />

                        {/* Icon Card */}
                        <div className="relative bg-white/10 backdrop-blur-xl rounded-[1.5rem] p-6 shadow-2xl border border-white/20">
                          <IconComponent />
                        </div>
                      </div>
                    </div>

                    {/* Text Content */}
                    <h2 className="text-2xl font-bold mb-2 text-center tracking-tight drop-shadow-lg">
                      {role.title}
                    </h2>
                    <p className="text-sm opacity-90 text-center max-w-[200px] font-light leading-relaxed drop-shadow">
                      {role.description}
                    </p>

                    {/* Hover Indicator */}
                    <div className={`mt-6 transition-all duration-500 ${
                      isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                    }`}>
                      <div className="flex items-center gap-2 px-5 py-2.5 bg-white/25 backdrop-blur-lg rounded-full shadow-xl">
                        <span className="text-xs font-semibold tracking-wide">Get Started</span>
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}

                {/* Content - Login Form View */}
                {isSelected && (
                  <div className="relative h-full flex items-center justify-center p-8">
                    {/* Darkened Overlay for Better Contrast */}
                    <div className="absolute inset-0 bg-black/40 backdrop-blur-md" />

                    {/* Back Button */}
                    <button
                      onClick={handleBack}
                      className="absolute top-6 left-6 z-10 text-white hover:bg-white/30 rounded-full p-3 transition-all duration-300
                                 backdrop-blur-md border-2 border-white/40 hover:scale-110 shadow-xl"
                      style={{
                        animation: 'contentFadeIn 0.5s cubic-bezier(0.54, 0.35, 0.29, 0.99) forwards',
                        animationDelay: '0.2s',
                        opacity: 0
                      }}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>

                    {/* Login Card */}
                    <div
                      className="relative bg-white/95 backdrop-blur-xl rounded-[2rem] shadow-2xl p-10 max-w-md w-full"
                      style={{
                        animation: 'cardSlideIn 0.8s cubic-bezier(0.25, 1, 0.5, 1.25) forwards',
                        animationDelay: '0.4s',
                        opacity: 0
                      }}
                    >
                      {/* Role Icon & Title */}
                      <div
                        className="text-center mb-8"
                        style={{
                          animation: 'contentFadeIn 0.6s cubic-bezier(0.54, 0.35, 0.29, 0.99) forwards',
                          animationDelay: '0.7s',
                          opacity: 0
                        }}
                      >
                        <div className="relative inline-block mb-6">
                          {/* Icon Glow */}
                          <div className="absolute inset-0 blur-3xl opacity-40" style={{ backgroundColor: role.accentColor }} />
                          {/* Icon Container */}
                          <div className={`relative bg-gradient-to-br ${role.iconGradient} rounded-[1.5rem] p-6 shadow-2xl`}>
                            <div className="text-white">
                              <IconComponent />
                            </div>
                          </div>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight">
                          {role.title}
                        </h2>
                        <p className="text-gray-500 text-base">
                          Sign in to continue
                        </p>
                      </div>

                      {/* Action Buttons */}
                      <div
                        className="space-y-4"
                        style={{
                          animation: 'contentFadeIn 0.6s cubic-bezier(0.54, 0.35, 0.29, 0.99) forwards',
                          animationDelay: '0.9s',
                          opacity: 0
                        }}
                      >
                        <button
                          onClick={() => handleLogin(role.path)}
                          className={`w-full bg-gradient-to-r ${role.buttonGradient} text-white font-semibold py-4 px-6 rounded-2xl
                                     hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 text-base tracking-wide`}
                        >
                          Login
                        </button>

                        {/* Hide Create Account for Platform Admin */}
                        {role.id !== 'super-admin' && (
                          <button
                            onClick={() => handleRegister(role.registerPath)}
                            className="w-full bg-gray-100 text-gray-800 font-semibold py-4 px-6 rounded-2xl
                                     hover:bg-gray-200 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 text-base"
                          >
                            Create Account
                          </button>
                        )}
                      </div>

                      {/* Divider */}
                      <div
                        className="relative my-8"
                        style={{
                          animation: 'contentFadeIn 0.6s cubic-bezier(0.54, 0.35, 0.29, 0.99) forwards',
                          animationDelay: '1.1s',
                          opacity: 0
                        }}
                      >
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                          <span className="px-4 bg-white text-gray-400">Need assistance?</span>
                        </div>
                      </div>

                      {/* Help Text */}
                      <div
                        className="text-center"
                        style={{
                          animation: 'contentFadeIn 0.6s cubic-bezier(0.54, 0.35, 0.29, 0.99) forwards',
                          animationDelay: '1.2s',
                          opacity: 0
                        }}
                      >
                        <p className="text-sm text-gray-500 flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Contact your school administrator
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Bottom Tagline - Only show when no role selected */}
      {!selectedRole && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-50 text-white text-center">
          <div className="bg-white/10 backdrop-blur-2xl border border-white/20 rounded-full px-8 py-3 shadow-2xl">
            <p className="text-sm font-light tracking-wide drop-shadow-lg">
              Learn, Discover & Grow with AI
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleSelectionPage;
